import api, { getErrorMessage } from '@/services/api';
import { formatDate } from '@/utils';

/**
 * Multi-insurer premium options on quote creation.
 *
 * The broker can list several insurance companies against one quote request --
 * Insurance Company, Product, Amount, Expiry Date and an optional attachment --
 * and each one is POSTed to `/quotes/requests/{id}/options/`, the manual
 * counterpart to the live GoDigit motor pricing path. Both write the SAME
 * `Quote` rows, so a request can carry a mix of API-priced and hand-entered
 * options and the compare screen, the public link and the accept flow treat
 * them identically (see backend/apps/quotes/views.py::premium_options).
 *
 * Options are held as local drafts while the wizard runs -- there is no quote
 * request to hang them off until submit succeeds -- and flushed afterwards.
 */

export interface PremiumOptionDraft {
  /** Local-only React key. Never sent. */
  uid: string;
  /** Insurer UUID. */
  insurer: string;
  insurer_name: string;
  /** Product UUID (scoped to the insurer + insurance type). */
  product: string;
  product_name: string;
  /** Free-typed amount; kept as a string like every other field in this wizard. */
  total_premium: string;
  /** `YYYY-MM-DD` straight out of `<input type="date">`. */
  valid_until: string;
  file: File | null;
}

let uidCounter = 0;

/**
 * Local list key. Deliberately not `crypto.randomUUID()` -- this never leaves
 * the browser, and a counter is stable under jsdom and old Safari alike.
 */
export function nextPremiumOptionUid(): string {
  uidCounter += 1;
  return `opt-${uidCounter}`;
}

export function emptyPremiumOption(): PremiumOptionDraft {
  return {
    uid: nextPremiumOptionUid(),
    insurer: '',
    insurer_name: '',
    product: '',
    product_name: '',
    total_premium: '',
    valid_until: '',
    file: null,
  };
}

/** True when nothing at all has been typed into this row. */
export function isPremiumOptionBlank(option: PremiumOptionDraft): boolean {
  return (
    !option.insurer &&
    !option.product &&
    !option.total_premium.trim() &&
    !option.valid_until &&
    !option.file
  );
}

/**
 * Field-keyed validation errors for one row, mirroring
 * `QuoteOptionCreateSerializer`'s required set (insurer, product,
 * total_premium, valid_until) plus its `total_premium > 0` and
 * `valid_until > now` rules -- so the broker sees them inline instead of as a
 * 400 after the quote request has already been created.
 *
 * `today` is injected so the expiry rule is testable without freezing time.
 */
export function premiumOptionErrors(
  option: PremiumOptionDraft,
  today: string = localDateKey(new Date())
): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!option.insurer) errors.insurer = 'Select an insurance company';
  if (!option.product) errors.product = 'Select a product';
  return { ...errors, ...premiumFigureErrors(option.total_premium, option.valid_until, today) };
}

/**
 * Just the amount and expiry rules.
 *
 * Split out of `premiumOptionErrors` because task 14 edits those two fields on
 * an option that ALREADY exists, where insurer and product are not in play. One
 * copy of the rule, so the in-place edit on the quote screen cannot drift away
 * from the add form or from `QuoteOptionCreateSerializer`.
 */
export function premiumFigureErrors(
  totalPremium: string,
  validUntil: string,
  today: string = localDateKey(new Date())
): Record<string, string> {
  const errors: Record<string, string> = {};

  const raw = totalPremium.trim();
  if (!raw) {
    errors.total_premium = 'Enter the premium amount';
  } else {
    const amount = Number(raw);
    if (!Number.isFinite(amount)) errors.total_premium = 'Enter a valid amount';
    else if (amount <= 0) errors.total_premium = 'Amount must be more than 0';
  }

  if (!validUntil) {
    errors.valid_until = 'Choose an expiry date';
  } else if (validUntil < today) {
    // String compare is safe: both sides are zero-padded `YYYY-MM-DD`.
    errors.valid_until = 'The expiry date must be today or later';
  }

  return errors;
}

export function isPremiumOptionValid(
  option: PremiumOptionDraft,
  today?: string
): boolean {
  return Object.keys(premiumOptionErrors(option, today)).length === 0;
}

/** Local `YYYY-MM-DD` (NOT `toISOString()`, which shifts the day in IST). */
export function localDateKey(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/**
 * `valid_until` is a DateTimeField the backend rejects unless it is strictly in
 * the future. A bare `YYYY-MM-DD` parses to midnight, so picking *today* -- the
 * obvious thing to do for a same-day quote -- would 400. Anchor it to the end
 * of the chosen day instead.
 */
export function expiryToTimestamp(day: string): string {
  return `${day}T23:59:59`;
}

/**
 * The inverse of `expiryToTimestamp`, for prefilling `<input type="date">` when
 * an option that already exists is edited or copied (task 14).
 *
 * UTC ON PURPOSE, and this is the one thing to get right here. The day the
 * broker picked is sent as a NAIVE `YYYY-MM-DDT23:59:59` and the server's
 * `TIME_ZONE` is "UTC" (config/settings/base.py), so it is stored and echoed
 * back as that same UTC calendar day at 23:59. Reading it back through the
 * BROWSER's calendar would roll every such option forward a day for anyone east
 * of Greenwich -- which is every broker on this platform.
 */
export function expiryDayFromTimestamp(iso: string | null | undefined): string {
  if (!iso) return '';
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${parsed.getUTCFullYear()}-${pad(parsed.getUTCMonth() + 1)}-${pad(parsed.getUTCDate())}`;
}

/**
 * The stored expiry, rendered as THE DAY THE BROKER PICKED.
 *
 * `formatDate` reads a stamp through the BROWSER's calendar. Every expiry this
 * platform writes is `...T23:59:59` in UTC, so east of Greenwich that reading
 * lands on the following day -- and the card showed "Oct 01" over an editor
 * prefilled with 2026-09-30. A broker who "corrected" the editor to match the
 * figure stored a day later, and the figure moved again: every edit walked the
 * date forward by one.
 *
 * So there is exactly one reading of a stored expiry, and it is
 * `expiryDayFromTimestamp`'s -- the UTC calendar day, which is the day
 * `expiryToTimestamp` sent. Anything that DISPLAYS `valid_until` must come
 * through here; `formatDate` on the raw stamp is the bug.
 */
export function formatExpiryDay(iso: string | null | undefined): string {
  const day = expiryDayFromTimestamp(iso);
  if (!day) return '';
  const [year, month, date] = day.split('-').map(Number);
  // Local NOON: `formatDate` reads this back in the browser's zone, and midday
  // keeps the same calendar day in every offset and across a DST jump.
  return formatDate(new Date(year, month - 1, date, 12, 0, 0));
}

export function premiumOptionToFormData(option: PremiumOptionDraft): FormData {
  const form = new FormData();
  form.append('insurer', option.insurer);
  form.append('product', option.product);
  form.append('total_premium', option.total_premium.trim());
  form.append('valid_until', expiryToTimestamp(option.valid_until));
  if (option.file) {
    form.append('file', option.file);
    form.append('document_name', option.file.name);
  }
  return form;
}

export interface PremiumOptionsResult {
  created: number;
  failures: { label: string; message: string }[];
}

/**
 * Flush the drafts onto a freshly created quote request.
 *
 * Sent one at a time and on purpose: a partial failure must not take the whole
 * batch (or the already-created quote request) down with it. The caller reports
 * whatever failed and leaves the request in place -- options can always be
 * added again from the quote detail page.
 */
export async function postPremiumOptions(
  quoteRequestId: string,
  options: PremiumOptionDraft[]
): Promise<PremiumOptionsResult> {
  const result: PremiumOptionsResult = { created: 0, failures: [] };
  for (const option of options) {
    if (isPremiumOptionBlank(option)) continue;
    try {
      await api.post(`/quotes/requests/${quoteRequestId}/options/`, premiumOptionToFormData(option), {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      result.created += 1;
    } catch (error) {
      result.failures.push({
        label: option.insurer_name || 'Premium option',
        message: getErrorMessage(error),
      });
    }
  }
  return result;
}
